// VoiceSpectrum.Web.T2 · v12.10.9.1 (#1272 restore a stored session · #1286 one session across tabs · #1271 hours) · 2026-10-06
// v12.10.9.1 (#1272): opening the site with an EXPIRED stored access token no longer throws it away. The 60-minute token
// is not the session - the server-side session is (Keep me signed in = Remember for days). So the provider first asks
// /auth/refresh with the stored token (the server accepts an expired one and checks the session); only a 401 ends it.
// While that answer is pending `restoring` is true and RequireAuth renders nothing instead of bouncing to /login.
// v12.10.9.1 (#1286): every tab shares one session and one stored token. A tab that signs out (idle, Sign out) clears
// the token; the other tabs hear the 'storage' event and sign out locally too (no second API call).
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1255/#1256/#1257) · 2026-10-06
// v12.10.9: L12 "Signed out" - a refresh answered 401 {error:'session_ended', reason} records WHY (idle / absolute /
// revoked / access_changed, + idle minutes when the body carries them) for the sign-in page's banner, then signs out
// locally WITHOUT calling the API (the session is already gone). An explicit logout() now POSTs /api/auth/logout first
// (best effort) so the server revokes this session. markSignedOut() lets the idle guard record "idle" the same way.
// Authentication context, hooks, and route guard
// VoiceSpectrum.Web.T2 · v12.10.7.1 (#1215 the sign-in page says the session ended) · 2026-10-05
// v12.1.31.0 · 2026-07-06 · VoiceSpectrum AI
//
// v12.1.28.0 (SES1-O): proactive token refresh keeps ACTIVE users signed in. Instead of a
// silent hard logout the moment the JWT hits its absolute lifetime, we slide the token: on an
// interval AND on genuine user activity, if the token is within REFRESH_THRESHOLD_SEC of `exp`
// we POST /auth/refresh and swap in the fresh token. We only logout() when a refresh attempt
// fails against an already-dead token (401 / expired-and-refresh-failed). A genuinely idle user
// is still signed out by IdleLogoutGuard (and eventually a failed refresh).
//
// v12.1.31.0 (KK — silent logout ROOT CAUSE): the API's RefreshAsync requires the current
// `accessToken` in the POST body (returns 400 without it), so the old body-less
// `api.post('/auth/refresh')` NEVER succeeded → the token never slid → active users were signed
// out silently at expiry. FIX: send the current token in the body. We mirror the latest token in
// a `tokenRef` (like `claimsRef`) so the refresh loop always posts the live token.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, setToken, clearToken, ApiError, SESSION_ENDED_EVENT } from './api';
import { authApi } from './authApi';

// ── JWT claim shape ──────────────────────────────────────────────────────────
export interface JwtClaims {
  sub: string;
  email: string;
  tenant_id: string;
  tenant_slug: string;
  org_id: string;
  role: string;
  permissions?: string | string[];
  exp: number;
  iat: number;
}

// ── Auth state ───────────────────────────────────────────────────────────────
interface AuthState {
  token: string | null;
  claims: JwtClaims | null;
}

interface AuthContextValue extends AuthState {
  login: (token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  /** v12.10.9.1 (#1272): true while a stored, expired token is being renewed from its server session. */
  restoring: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseJwt(token: string): JwtClaims | null {
  try {
    const [, payload] = token.split('.');
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return normalizeClaims(JSON.parse(decoded));
  } catch {
    return null;
  }
}

function firstClaim(raw: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = raw[key];
    if (Array.isArray(value)) {
      const first = value.find((item) => typeof item === 'string' && item.trim());
      if (typeof first === 'string') return first;
    }
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return '';
}

function normalizeClaims(rawClaims: unknown): JwtClaims {
  const raw = (rawClaims ?? {}) as Record<string, unknown>;
  const email = firstClaim(raw, [
    'email',
    'sub',
    'nameid',
    'unique_name',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
  ]);
  const role = firstClaim(raw, [
    'role',
    'roles',
    'vs_role',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  ]);
  const permissions = raw.permissions ?? raw.permission ?? raw.vs_permissions ?? raw.perms;

  return {
    ...(raw as Partial<JwtClaims>),
    sub: firstClaim(raw, ['sub']) || email,
    email,
    tenant_id: firstClaim(raw, ['tenant_id', 'vs_tenant_id', 'tenantId']),
    tenant_slug: firstClaim(raw, ['tenant_slug', 'vs_tenant_slug', 'tenantSlug']),
    org_id: firstClaim(raw, ['org_id', 'vs_org_id', 'organization_id', 'orgId']),
    role,
    permissions: permissions as string | string[] | undefined,
    exp: Number(raw.exp ?? 0),
    iat: Number(raw.iat ?? 0),
  } as JwtClaims;
}

function isTokenExpired(claims: JwtClaims): boolean {
  return Date.now() / 1000 > claims.exp;
}

// Seconds remaining before `exp` at which we proactively refresh the token.
const REFRESH_THRESHOLD_SEC = 120;
// How often the background loop checks whether a refresh is due.
const REFRESH_CHECK_INTERVAL_MS = 45_000;

function secondsUntilExpiry(claims: JwtClaims): number {
  return claims.exp - Date.now() / 1000;
}

const TOKEN_KEY = 'vs_t2_token';
// v12.10.7.1 (#1215): set when a stored sign-in has run out, read once by the sign-in page
// ("Your session ended - sign in again") so a prompt that remains says why.
export const SESSION_ENDED_KEY = 'vs_t2_session_ended';
function markSessionEnded() { try { sessionStorage.setItem(SESSION_ENDED_KEY, '1'); } catch { /* storage blocked */ } }

// v12.10.9 (L12): why the person was signed out. Stored as JSON under SESSION_ENDED_KEY ('1' = the older "session ended").
export type SignedOutReason = 'idle' | 'absolute' | 'revoked' | 'access_changed' | 'ended';
export interface SignedOutNotice { reason: SignedOutReason; minutes?: number; hours?: number }
export function markSignedOut(reason: SignedOutReason, minutes?: number, hours?: number): void {
  try { sessionStorage.setItem(SESSION_ENDED_KEY, JSON.stringify({ reason, ...(minutes && minutes > 0 ? { minutes } : {}), ...(hours && hours > 0 ? { hours } : {}) })); }
  catch { /* storage blocked */ }
}
/** Read once and clear (the sign-in page shows it a single time). */
export function takeSignedOutNotice(): SignedOutNotice | null {
  try {
    const v = sessionStorage.getItem(SESSION_ENDED_KEY);
    sessionStorage.removeItem(SESSION_ENDED_KEY);
    if (!v) return null;
    if (v === '1') return { reason: 'ended' };
    const o = JSON.parse(v) as Partial<SignedOutNotice>;
    const known: SignedOutReason[] = ['idle', 'absolute', 'revoked', 'access_changed', 'ended'];
    const reason = known.includes(o.reason as SignedOutReason) ? (o.reason as SignedOutReason) : 'ended';
    return { reason, ...(typeof o.minutes === 'number' && o.minutes > 0 ? { minutes: o.minutes } : {}), ...(typeof o.hours === 'number' && o.hours > 0 ? { hours: o.hours } : {}) };
  } catch { return null; }
}
/** 401 {error:'session_ended', reason, idleMinutes?} from /auth/refresh -> the notice to record, else null. */
function sessionEndedNotice(err: unknown): SignedOutNotice | null {
  if (!(err instanceof ApiError) || err.status !== 401 || !err.body || typeof err.body !== 'object') return null;
  const b = err.body as { error?: string; reason?: string; idleMinutes?: number; minutes?: number; absoluteHours?: number };
  if (b.error !== 'session_ended') return null;
  const reason: SignedOutReason = b.reason === 'idle' || b.reason === 'absolute' || b.reason === 'revoked' || b.reason === 'access_changed' ? b.reason : 'ended';
  const minutes = typeof b.idleMinutes === 'number' ? b.idleMinutes : typeof b.minutes === 'number' ? b.minutes : undefined;
  const hours = typeof b.absoluteHours === 'number' ? b.absoluteHours : undefined;
  return { reason, ...(minutes && minutes > 0 ? { minutes } : {}), ...(hours && hours > 0 ? { hours } : {}) };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // v12.10.9.1 (#1272): an expired stored token is kept aside and renewed from its session (effect below).
  const staleRef = useRef<string | null>(null);
  const [state, setState] = useState<AuthState>(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return { token: null, claims: null };
    const claims = parseJwt(stored);
    if (!claims) { localStorage.removeItem(TOKEN_KEY); return { token: null, claims: null }; }
    if (isTokenExpired(claims)) { staleRef.current = stored; return { token: null, claims: null }; }
    return { token: stored, claims };
  });
  const [restoring, setRestoring] = useState<boolean>(() => staleRef.current !== null);

  useEffect(() => {
    const stale = staleRef.current;
    if (!stale) return;
    staleRef.current = null;
    let cancelled = false;
    api.post<{ accessToken?: string }>('/auth/refresh', { accessToken: stale })
      .then((resp) => {
        if (cancelled) return;
        const fresh = resp?.accessToken;
        const c = fresh ? parseJwt(fresh) : null;
        if (fresh && c) { setToken(fresh); setState({ token: fresh, claims: c }); }
        else { localStorage.removeItem(TOKEN_KEY); markSessionEnded(); }
      })
      .catch((err) => {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_KEY);
        const ended = sessionEndedNotice(err);
        if (ended) markSignedOut(ended.reason, ended.minutes, ended.hours); else markSessionEnded();
      })
      .finally(() => { if (!cancelled) setRestoring(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback((token: string) => {
    const claims = parseJwt(token);
    if (!claims) return;
    setToken(token);
    setState({ token, claims });
  }, []);

  // Latest claims accessible from the refresh loop / activity handlers without re-subscribing.
  const claimsRef = useRef<JwtClaims | null>(state.claims);
  claimsRef.current = state.claims;
  // Latest token — RefreshAsync requires the current access token in the POST body (KK).
  const tokenRef = useRef<string | null>(state.token);
  tokenRef.current = state.token;

  // Local sign-out only (the session is already over on the server).
  const endLocal = useCallback(() => {
    clearToken();
    setState({ token: null, claims: null });
  }, []);

  // v12.10.9: explicit sign-out revokes this session server-side (POST /api/auth/logout), best effort, then clears.
  const logout = useCallback(() => {
    const t = tokenRef.current;
    if (t) authApi.logout(t).catch(() => { /* already gone / offline - local sign-out still happens */ });
    endLocal();
  }, [endLocal]);
  // Guards against overlapping refresh calls (interval + activity firing together).
  const refreshingRef = useRef(false);

  // Proactive token refresh: slide the token for ACTIVE users, only sign out on a hard failure.
  useEffect(() => {
    if (!state.token) return;
    let cancelled = false;

    // Attempt a refresh when the token is within threshold of expiry. Returns silently on success;
    // logs the user out only when the token is truly dead (401, or expired and refresh failed).
    const maybeRefresh = async () => {
      const claims = claimsRef.current;
      if (!claims || cancelled || refreshingRef.current) return;

      const remaining = secondsUntilExpiry(claims);
      const expired = remaining <= 0;
      if (!expired && remaining > REFRESH_THRESHOLD_SEC) return; // plenty of life left

      refreshingRef.current = true;
      try {
        // RefreshAsync requires the current accessToken in the body — posting without it returns
        // 400 and the refresh never succeeds (KK). Send the live token so the slide can happen.
        const currentToken = tokenRef.current;
        const resp = await api.post<{ accessToken?: string }>('/auth/refresh', {
          accessToken: currentToken,
        });
        const fresh = resp?.accessToken;
        if (cancelled) return;
        if (fresh) {
          const nextClaims = parseJwt(fresh);
          if (nextClaims) {
            setToken(fresh);
            setState({ token: fresh, claims: nextClaims });
            return;
          }
        }
        // Malformed refresh response — treat like a failure only if the token is already dead.
        if (expired) { markSessionEnded(); endLocal(); }
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : 0;
        // v12.10.9 (L12): the server ended the session (idle / absolute / revoked / access changed) - say why.
        const ended = sessionEndedNotice(err);
        if (ended) { markSignedOut(ended.reason, ended.minutes, ended.hours); endLocal(); return; }
        // 401 → token is truly dead / user disabled: sign out. Same if the token already expired
        // and we couldn't renew it. Transient errors (network/5xx) on a still-valid token are
        // swallowed so we don't drop an active user; the next tick retries.
        if (status === 401 || expired) { markSessionEnded(); endLocal(); }
      } finally {
        // Always release the in-flight guard, even if this effect instance was torn down
        // (e.g. the token changed on success and a new effect took over).
        refreshingRef.current = false;
      }
    };

    const interval = window.setInterval(() => { void maybeRefresh(); }, REFRESH_CHECK_INTERVAL_MS);

    // Genuine user activity also triggers an eager refresh check so an active user's token slides
    // well before it can lapse.
    const onActivity = () => { void maybeRefresh(); };
    const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    // Run once on mount in case we loaded a token that's already near expiry.
    void maybeRefresh();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [state.token, endLocal]);

  // v12.10.9 (L12): any API call told us the server ended this session - record why and sign out now.
  useEffect(() => {
    if (!state.token) return;
    const onEnded = (e: Event) => {
      const d = (e as CustomEvent<{ reason?: string; idleMinutes?: number; absoluteHours?: number }>).detail ?? {};
      const reason: SignedOutReason = d.reason === 'idle' || d.reason === 'absolute' || d.reason === 'revoked' || d.reason === 'access_changed' ? d.reason : 'ended';
      markSignedOut(reason, typeof d.idleMinutes === 'number' ? d.idleMinutes : undefined, typeof d.absoluteHours === 'number' ? d.absoluteHours : undefined);
      endLocal();
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, [state.token, endLocal]);

  // v12.10.9.1 (#1286): another tab signed out (token removed) or signed in again (a new token) - follow it.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== TOKEN_KEY) return;
      if (!e.newValue) {
        if (tokenRef.current) { setState({ token: null, claims: null }); }
        return;
      }
      if (e.newValue !== tokenRef.current) {
        const c = parseJwt(e.newValue);
        if (c) setState({ token: e.newValue, claims: c });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        restoring,
        isAuthenticated: state.token !== null && state.claims !== null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ── Route guard ──────────────────────────────────────────────────────────────
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, restoring } = useAuth();
  const location = useLocation();

  if (restoring) return null;   // v12.10.9.1 (#1272): renewing a stored session - do not bounce to /login yet
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export function useTenantPermissions() {
  const { claims } = useAuth();
  const raw = claims?.permissions;
  const permissions = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(/[,\s]+/).filter(Boolean)
      : [];
  const role = (claims?.role ?? '').toLowerCase();
  const isAdmin = role.includes('admin');

  const has = useCallback((permission: string) => {
    if (isAdmin) return true;
    return permissions.includes(permission) || permissions.includes('*');
  }, [isAdmin, permissions]);

  return {
    role,
    permissions,
    isAdmin,
    canMessages: isAdmin || has('messages.view'),
    canReports: isAdmin || has('reports.view'),
    canConfigure: isAdmin || has('config.view') || has('workflow.edit') || has('users.manage') || has('security.manage'),
    has,
  };
}
